/**
 * Places PrimeVue dialogs as they open.
 *
 * PrimeVue's dialog mask is a full screen flex container that centres every dialog in the
 * very same spot, and the dialogs passing position="top" land 1rem from the top of the
 * viewport - on top of the fixed topbar. Instead each dialog is pinned where the click that
 * opened it was, cascaded a little for every dialog already open, and always kept below the
 * topbar and inside the viewport. Pinning uses the same inline properties as PrimeVue's own
 * drag handler (position/margin/left/top), so a dialog can still be dragged from there.
 *
 * Dialogs carrying the class `no-auto-position` - SettingsModal and CalendarModal - keep
 * PrimeVue's centring. They are kept clear of the topbar by the `.p-dialog-mask` padding
 * rule in base.css, which reads the `--topbar-height` variable published from here.
 */

/** A point in viewport coordinates */
export interface DialogPositionPoint {
	x: number
	y: number
}

/** Size of a dialog, or of the viewport */
export interface DialogSize {
	width: number
	height: number
}

/** Everything needed to place a dialog that is about to open */
export interface DialogPositionInput {
	/** Where the click that opened the dialog was, or null when no recent click can be credited for it */
	pointer: DialogPositionPoint | null
	/** Size the dialog laid out at while it was still centred by the mask */
	size: DialogSize
	viewport: DialogSize
	/** Bottom edge of the topbar, 0 when no topbar is showing */
	topbarBottom: number
	/** Number of dialogs already open, each of which pushes this one further down and right */
	openCount: number
}

/** Where a dialog ends up, in viewport coordinates */
export interface DialogPosition {
	left: number
	top: number
	maxHeight: number
}

/** Gap kept between a dialog and the viewport edges, and between a dialog and the topbar */
export const DIALOG_POSITION_MARGIN = 12

/** Every dialog already open pushes the next one this far down and to the right */
export const DIALOG_POSITION_CASCADE_STEP = 28

/** Cascade steps stop accumulating here, so a long stack cannot march away from the pointer */
export const DIALOG_POSITION_CASCADE_MAX_STEPS = 6

/** How far above the pointer the top edge goes, so the dialog appears to come out of what was clicked */
const POINTER_TOP_OFFSET = 32

/** A click older than this did not open the dialog - something asynchronous did */
const POINTER_MAX_AGE_MS = 1500

/** Dialogs with this class keep PrimeVue's own centring */
const OPT_OUT_CLASS = 'no-auto-position'

/** Marks the dialogs this module has placed, so resizes only touch those */
const POSITIONED_ATTRIBUTE = 'data-tcx-positioned'

/**
 * Clamp a value, preferring the minimum when there is no room to satisfy both bounds
 * (a dialog wider or taller than the space available)
 */
function clamp(value: number, min: number, max: number): number {
	if (max < min) {
		return min
	}
	return Math.min(Math.max(value, min), max)
}

/**
 * Pull a position back inside the viewport and below the topbar, and work out how tall the
 * dialog may become without growing past the bottom margin
 */
export function clampDialogPosition(input: { left: number, top: number, size: DialogSize, viewport: DialogSize, topbarBottom: number }): DialogPosition {
	const { left, top, size, viewport, topbarBottom } = input
	const clampedLeft = clamp(left, DIALOG_POSITION_MARGIN, viewport.width - size.width - DIALOG_POSITION_MARGIN)
	const clampedTop = clamp(top, topbarBottom + DIALOG_POSITION_MARGIN, viewport.height - size.height - DIALOG_POSITION_MARGIN)

	return {
		left: Math.round(clampedLeft),
		top: Math.round(clampedTop),
		maxHeight: Math.round(viewport.height - clampedTop - DIALOG_POSITION_MARGIN)
	}
}

/**
 * Work out where a dialog that is about to open should go: centred on the pointer that
 * opened it, or centred below the topbar when no pointer can be credited for it, cascaded
 * for the dialogs already open and clamped into view
 */
export function computeDialogPosition(input: DialogPositionInput): DialogPosition {
	const { pointer, size, viewport, topbarBottom, openCount } = input
	const cascade = Math.min(openCount, DIALOG_POSITION_CASCADE_MAX_STEPS) * DIALOG_POSITION_CASCADE_STEP
	const left = pointer ? pointer.x - (size.width / 2) : (viewport.width - size.width) / 2
	const top = pointer ? pointer.y - POINTER_TOP_OFFSET : topbarBottom + DIALOG_POSITION_MARGIN

	return clampDialogPosition({ left: left + cascade, top: top + cascade, size, viewport, topbarBottom })
}

class DialogPositioner {
	/** The last click, and when it happened */
	private pointer: { x: number, y: number, at: number } | null = null

	private readonly observer: MutationObserver

	/** Set while a resize re-clamp is already queued for the next frame */
	private resizeQueued = false

	private constructor() {
		document.addEventListener('pointerdown', this.onPointerDown, { capture: true, passive: true })
		window.addEventListener('resize', this.onResize)
		this.observer = new MutationObserver(this.onMutations)
		this.observer.observe(document.body, { childList: true })
		this.publishTopbarHeight()
	}

	private onPointerDown = (event: PointerEvent) => {
		this.pointer = { x: event.clientX, y: event.clientY, at: performance.now() }
	}

	/**
	 * PrimeVue appends a dialog mask to the body when a dialog opens. The callback runs
	 * before the browser gets to paint, so the dialog is placed without ever being seen in
	 * the centre of the mask.
	 */
	private onMutations = (mutations: MutationRecord[]) => {
		for (const mutation of mutations) {
			for (const node of Array.from(mutation.addedNodes)) {
				if (!(node instanceof HTMLElement)) {
					continue
				}
				const dialog = node.classList.contains('p-dialog') ? node : node.querySelector<HTMLElement>('.p-dialog')
				if (dialog) {
					this.place(dialog)
				}
			}
		}
	}

	private place(dialog: HTMLElement) {
		if (dialog.hasAttribute(POSITIONED_ATTRIBUTE) || dialog.classList.contains(OPT_OUT_CLASS)) {
			return
		}

		// offsetWidth/offsetHeight rather than a bounding rect: the dialog is on the first
		// frame of its scale(0.93) enter animation, and a rect would come back scaled with it
		const size = { width: dialog.offsetWidth, height: dialog.offsetHeight }
		const position = computeDialogPosition({
			pointer: this.recentPointer(),
			size,
			viewport: { width: window.innerWidth, height: window.innerHeight },
			topbarBottom: this.publishTopbarHeight(),
			openCount: this.countOpenDialogs(dialog)
		})

		dialog.setAttribute(POSITIONED_ATTRIBUTE, 'true')
		this.apply(dialog, position)
	}

	/** The properties PrimeVue's drag handler uses, so dragging carries on from where we put it */
	private apply(dialog: HTMLElement, position: DialogPosition) {
		dialog.style.position = 'fixed'
		dialog.style.margin = '0'
		dialog.style.left = `${position.left}px`
		dialog.style.top = `${position.top}px`
		dialog.style.maxHeight = `${position.maxHeight}px`
	}

	/** The last click, unless it is too old to be what opened this dialog */
	private recentPointer(): DialogPositionPoint | null {
		if (!this.pointer || performance.now() - this.pointer.at > POINTER_MAX_AGE_MS) {
			return null
		}
		return { x: this.pointer.x, y: this.pointer.y }
	}

	/** Dialogs open right now, ignoring the one opening and any still playing their leave transition */
	private countOpenDialogs(opening: HTMLElement): number {
		return Array.from(document.querySelectorAll<HTMLElement>('.p-dialog'))
			.filter(dialog => dialog !== opening
				&& !dialog.classList.contains('p-dialog-leave-from')
				&& !dialog.classList.contains('p-dialog-leave-active'))
			.length
	}

	/**
	 * Measure the topbar - it is fixed to the top, so its height is its bottom edge - and
	 * publish it for the `.p-dialog-mask` rule that keeps the centred dialogs off it. Height
	 * is 0 while no topbar is showing, as on the login screen.
	 */
	private publishTopbarHeight(): number {
		const topbar = document.querySelector<HTMLElement>('.top-bar')
		const height = topbar ? topbar.offsetHeight : 0

		document.documentElement.style.setProperty('--topbar-height', `${height}px`)
		return height
	}

	/**
	 * Keep open dialogs where the user left them, but pull them back into a window that just
	 * shrank - and off a topbar that just grew a row taller
	 */
	private onResize = () => {
		if (this.resizeQueued) {
			return
		}
		this.resizeQueued = true

		requestAnimationFrame(() => {
			this.resizeQueued = false
			const topbarBottom = this.publishTopbarHeight()
			const viewport = { width: window.innerWidth, height: window.innerHeight }

			document.querySelectorAll<HTMLElement>(`.p-dialog[${POSITIONED_ATTRIBUTE}]`).forEach(dialog => {
				dialog.style.maxHeight = ''
				const size = { width: dialog.offsetWidth, height: dialog.offsetHeight }

				this.apply(dialog, clampDialogPosition({
					left: parseFloat(dialog.style.left) || 0,
					top: parseFloat(dialog.style.top) || 0,
					size,
					viewport,
					topbarBottom
				}))
			})
		})
	}

	/** Start placing dialogs */
	static create() {
		return new DialogPositioner()
	}
}

export default DialogPositioner
